import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container,
    Row,
    Col,
    Card,
    Table,
    Form,
    InputGroup,
    Badge,
    Button,
    Spinner,
    Alert,
    Pagination
} from 'react-bootstrap';
import { FaSearch, FaFilter, FaPlus, FaEye, FaEdit, FaTrash } from 'react-icons/fa';
import PageHeader from '../components/PageHeader';
import ConfirmationModal from '../components/ConfirmationModal';
import vendorService from '../services/vendorService';
// auth not required here; companies list uses vendorService directly

const ITEMS_PER_PAGE = 10;

function CompaniesPage() {
    const navigate = useNavigate();

    const [vendors, setVendors] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [companyToDelete, setCompanyToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [verifiedFilter, setVerifiedFilter] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [totalPages, setTotalPages] = useState(1);

    const fetchVendors = useCallback(async () => {
        try {
            setLoading(true);
            setError('');

            const params = {
                page: currentPage,
                per_page: ITEMS_PER_PAGE,
                sort_by: 'created_at',
                sort_order: 'desc'
            };

            if (searchQuery) {
                const searchData = await vendorService.search(searchQuery);
                setVendors(searchData.vendors || []);
                setTotalItems(searchData.vendors?.length || 0);
                setTotalPages(1);
                return;
            }

            if (statusFilter) params.status = statusFilter;
            if (verifiedFilter === 'verified') params.verified = 'true';
            else if (verifiedFilter === 'unverified') params.verified = 'false';

            const data = await vendorService.getAll(params);
            setVendors(data.vendors || []);
            setTotalItems(data.pagination?.total || 0);
            setTotalPages(data.pagination?.pages || 1);
        } catch (err) {
            console.error('Companies fetch failed:', err);
            setError('Failed to load companies.');
        } finally {
            setLoading(false);
        }
    }, [currentPage, searchQuery, statusFilter, verifiedFilter]);

    useEffect(() => {
        fetchVendors();
    }, [fetchVendors]);

    const handlePageChange = (page) => setCurrentPage(page);

    const getStatusVariant = (status) => {
        const variants = { active: 'success', inactive: 'secondary', pending: 'warning', error: 'danger' };
        return variants[status] || 'secondary';
    };

    const handleDeleteClick = (company) => {
        setCompanyToDelete(company);
        setShowDeleteConfirm(true);
    };

    const handleDeleteConfirm = async () => {
        if (!companyToDelete) return;
        
        setDeleting(true);
        try {
            await vendorService.delete(companyToDelete.id);
            setVendors(vendors.filter(v => v.id !== companyToDelete.id));
            setTotalItems(prev => prev - 1);
            setShowDeleteConfirm(false);
            setCompanyToDelete(null);
        } catch (err) {
            console.error('Delete failed:', err);
            setError('Failed to delete company');
            setShowDeleteConfirm(false);
        } finally {
            setDeleting(false);
        }
    };

    if (loading && vendors.length === 0) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <Spinner animation="border" variant="primary" />
            </div>
        );
    }

    return (
        <Container fluid className="px-4">
            <PageHeader title="Companies" subtitle={`${totalItems} companies`} />

            <Row className="g-3 mb-4">
                <Col md={5}>
                    <InputGroup>
                        <InputGroup.Text className="bg-white">
                            <FaSearch className="text-muted" />
                        </InputGroup.Text>
                        <Form.Control
                            type="search"
                            placeholder="Search companies by name or website..."
                            value={searchQuery}
                            onChange={(e) => {
                                setSearchQuery(e.target.value);
                                setCurrentPage(1);
                            }}
                        />
                    </InputGroup>
                </Col>
                <Col md={2}>
                    <InputGroup>
                        <InputGroup.Text className="bg-white">
                            <FaFilter className="text-muted" />
                        </InputGroup.Text>
                        <Form.Select
                            value={statusFilter}
                            onChange={(e) => {
                                setStatusFilter(e.target.value);
                                setCurrentPage(1);
                            }}
                        >
                            <option value="">All Statuses</option>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                            <option value="pending">Pending</option>
                        </Form.Select>
                    </InputGroup>
                </Col>
                <Col md={2}>
                    <Form.Select
                        value={verifiedFilter}
                        onChange={(e) => {
                            setVerifiedFilter(e.target.value);
                            setCurrentPage(1);
                        }}
                    >
                        <option value="">All Companies</option>
                        <option value="verified">Verified</option>
                        <option value="unverified">Unverified</option>
                    </Form.Select>
                </Col>
                <Col md={3}>
                    <div className="d-flex gap-2">
                        <Button variant="primary" onClick={() => navigate('/vendors/new')}>
                            <FaPlus />
                        </Button>
                    </div>
                </Col>
            </Row>

            {error && (
                <Alert variant="danger" dismissible onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            <Card className="border-0 shadow-sm">
                <Card.Body className="p-0">
                    <div className="table-responsive">
                        <Table hover className="mb-0">
                            <thead className="bg-light">
                                <tr>
                                    <th className="fw-semibold">ID</th>
                                    <th className="fw-semibold">Company</th>
                                    <th className="fw-semibold">Owner</th>
                                    <th className="fw-semibold">Status</th>
                                    <th className="fw-semibold">Verification</th>
                                    <th className="fw-semibold text-center">Products</th>
                                    <th className="fw-semibold text-center">Employees</th>
                                    <th className="fw-semibold">Created</th>
                                    <th className="fw-semibold text-end">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {vendors.length === 0 ? (
                                    <tr>
                                        <td colSpan="9" className="text-center py-5">
                                            <p className="text-muted mb-0">No companies found</p>
                                            <small className="text-muted">Try adjusting your filters or search query</small>
                                        </td>
                                    </tr>
                                ) : (
                                    vendors.map((vendor) => (
                                        <tr key={vendor.id}>
                                            <td>#{vendor.id}</td>
                                            <td>
                                                <div className="d-flex align-items-center">
                                                    {vendor.logo ? (
                                                        <img src={vendor.logo} alt="logo" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 6 }} className="me-3" />
                                                    ) : (
                                                        <div style={{ width: 40, height: 40 }} className="me-3 bg-light rounded d-flex align-items-center justify-content-center">
                                                            <small className="text-muted">Logo</small>
                                                        </div>
                                                    )}
                                                    <div>
                                                        <div className="fw-semibold">{vendor.name}</div>
                                                        {vendor.website && (
                                                            <small className="text-muted d-block">
                                                                <a href={vendor.website} target="_blank" rel="noopener noreferrer">
                                                                    {vendor.website}
                                                                </a>
                                                            </small>
                                                        )}
                                                        <small className="text-muted d-block">{vendor.phone1 || ''} {vendor.whatsapp ? `• WhatsApp: ${vendor.whatsapp}` : ''}</small>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <div>
                                                    <div className="fw-semibold">{vendor.owner?.username}</div>
                                                    <small className="text-muted">{vendor.owner?.email}</small>
                                                </div>
                                            </td>
                                            <td>
                                                <Badge bg={getStatusVariant(vendor.status)}>
                                                    {vendor.status.charAt(0).toUpperCase() + vendor.status.slice(1)}
                                                </Badge>
                                            </td>
                                            <td>
                                                {vendor.is_verified ? (
                                                    <Badge bg="success">Verified</Badge>
                                                ) : (
                                                    <Badge bg="secondary">Unverified</Badge>
                                                )}
                                            </td>
                                            <td className="text-center">
                                                <Badge bg="primary" pill>{vendor.product_count || 0}</Badge>
                                            </td>
                                            <td className="text-center">
                                                <Badge bg="info" pill>{vendor.employee_count || 0}</Badge>
                                            </td>
                                            <td>
                                                <small className="text-muted">{new Date(vendor.created_at).toLocaleDateString()}</small>
                                            </td>
                                            <td className="text-end">
                                                <div className="d-flex justify-content-end gap-1">
                                                    <Button variant="link" size="sm" className="text-primary p-1" onClick={() => navigate(`/companies/${vendor.id}`)} title="View details"><FaEye /></Button>
                                                    <Button variant="link" size="sm" className="text-warning p-1" onClick={() => navigate(`/companies/${vendor.id}/edit`)} title="Edit company"><FaEdit /></Button>
                                                    <Button variant="link" size="sm" className="text-danger p-1" onClick={() => handleDeleteClick(vendor)} title="Delete company"><FaTrash /></Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </Table>
                    </div>
                </Card.Body>
            </Card>

            {totalPages > 1 && (
                <div className="d-flex justify-content-center mt-4">
                    <Pagination>
                        <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPage === 1} />
                        <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
                        {[...Array(totalPages)].map((_, index) => {
                            const page = index + 1;
                            if (page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1)) {
                                return (
                                    <Pagination.Item key={page} active={page === currentPage} onClick={() => handlePageChange(page)}>{page}</Pagination.Item>
                                );
                            } else if (page === currentPage - 2 || page === currentPage + 2) {
                                return <Pagination.Ellipsis key={page} disabled />;
                            }
                            return null;
                        })}
                        <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
                        <Pagination.Last onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} />
                    </Pagination>
                </div>
            )}

            <ConfirmationModal
                show={showDeleteConfirm}
                onHide={() => setShowDeleteConfirm(false)}
                onConfirm={handleDeleteConfirm}
                title="Delete Company"
                message={`Are you sure you want to delete the company "${companyToDelete?.name}"? This action cannot be undone.`}
                confirmText="Delete"
                confirmVariant="danger"
                loading={deleting}
            />
        </Container>
    );
}

export default CompaniesPage;
